import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus, Trash2, Loader2, TrendingUp, DollarSign, Users, Target, Save, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { getBudgetItemsForType, calculateBudgetTotals } from "@/lib/budgetItems";
import { exportOperationsPdf } from "@/lib/operationsPdfExport";
import { toast } from "sonner";

interface RevenueEntry {
  id: string;
  entry_date: string; // YYYY-MM-DD
  revenue_usd: number;
  covers: number;
  avg_spend_usd: number;
  daily_target_usd: number | null;
}

function todayStr(): string {
  return new Date().toISOString().split("T")[0];
}

function lastNDays(n: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    out.push(d.toISOString().split("T")[0]);
  }
  return out;
}

function isThisWeek(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  const day = now.getDay() === 0 ? 6 : now.getDay() - 1; // Mon=0..Sun=6
  const monday = new Date(now);
  monday.setDate(now.getDate() - day);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 7);
  return d >= monday && d < sunday;
}

function isThisMonth(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

export default function DailyRevenueTracker() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const { onboarding } = useAppContext();

  const businessType = activeLocation?.business_type || onboarding.businessType || "restaurant";
  const city = (activeLocation?.city || onboarding.city || "").toLowerCase();
  const cs = getCurrencySymbol(onboarding.country || city);
  const fmt = (usd: number) => `${cs}${Math.round(convertFromUSD(usd, city)).toLocaleString()}`;

  // Estimate a default monthly revenue from budget data (working capital / 3 months
  // is a rough proxy if no projection exists; fall back to a conservative default).
  const defaultDailyTargetUsd = useMemo(() => {
    try {
      const items = getBudgetItemsForType(businessType);
      const totals = calculateBudgetTotals(items);
      // Use the midpoint of the budget range as a proxy for total startup cost.
      // Healthy F&B target: roughly 1.5× monthly opex revenue, where monthly
      // opex is ~5% of total startup. Daily target = monthly / 30.
      const mid = (totals.totalLow + totals.totalHigh) / 2;
      const monthlyRevenue = mid * 0.05 * 1.5;
      return Math.round(monthlyRevenue / 30);
    } catch {
      return 1500;
    }
  }, [businessType]);

  const [entries, setEntries] = useState<RevenueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<{
    entry_date: string; revenue: string; covers: string; avg_spend: string; target: string;
  }>({
    entry_date: todayStr(),
    revenue: "",
    covers: "",
    avg_spend: "",
    target: "",
  });

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    let q = supabase
      .from("daily_revenue_entries")
      .select("*")
      .eq("user_id", user.id)
      .order("entry_date", { ascending: false })
      .limit(60);
    if (activeLocation?.id) q = q.eq("location_id", activeLocation.id);
    else q = q.is("location_id", null);
    const { data, error } = await q;
    if (error) toast.error("Couldn't load revenue entries");
    setEntries(((data ?? []) as RevenueEntry[]));
    setLoading(false);
  }, [user, activeLocation?.id]);

  useEffect(() => { load(); }, [load]);

  // Convert from local currency input to USD for storage
  const toUSD = useCallback((local: number) => {
    const rate = convertFromUSD(1, city);
    return rate > 0 ? local / rate : local;
  }, [city]);

  const openAddDialog = () => {
    const existing = entries.find(e => e.entry_date === todayStr());
    if (existing) {
      // Edit today
      setDraft({
        entry_date: existing.entry_date,
        revenue: String(Math.round(convertFromUSD(existing.revenue_usd, city))),
        covers: String(existing.covers),
        avg_spend: String(Math.round(convertFromUSD(existing.avg_spend_usd, city) * 100) / 100),
        target: existing.daily_target_usd != null
          ? String(Math.round(convertFromUSD(existing.daily_target_usd, city)))
          : String(Math.round(convertFromUSD(defaultDailyTargetUsd, city))),
      });
    } else {
      setDraft({
        entry_date: todayStr(),
        revenue: "",
        covers: "",
        avg_spend: "",
        target: String(Math.round(convertFromUSD(defaultDailyTargetUsd, city))),
      });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!user) return;
    const revenue = Number(draft.revenue) || 0;
    const covers = Number(draft.covers) || 0;
    let avgSpend = Number(draft.avg_spend) || 0;
    if (avgSpend === 0 && covers > 0 && revenue > 0) avgSpend = revenue / covers;
    const target = draft.target ? Number(draft.target) : null;

    setSaving(true);
    const payload = {
      user_id: user.id,
      location_id: activeLocation?.id ?? null,
      entry_date: draft.entry_date,
      revenue_usd: toUSD(revenue),
      covers,
      avg_spend_usd: toUSD(avgSpend),
      daily_target_usd: target != null ? toUSD(target) : null,
    };
    const { error } = await supabase
      .from("daily_revenue_entries")
      .upsert(payload, { onConflict: "user_id,location_id,entry_date" });
    setSaving(false);
    if (error) {
      toast.error("Couldn't save entry");
      return;
    }
    toast.success("Daily revenue saved");
    setOpen(false);
    load();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("daily_revenue_entries").delete().eq("id", id);
    if (error) { toast.error("Couldn't delete entry"); return; }
    setEntries(prev => prev.filter(e => e.id !== id));
    toast.success("Entry removed");
  };

  // ─── Derived totals ──────────────────────────────────────────────────
  const today = entries.find(e => e.entry_date === todayStr());
  const todayTargetUsd = today?.daily_target_usd ?? defaultDailyTargetUsd;

  const weeklyActualUsd = useMemo(
    () => entries.filter(e => isThisWeek(e.entry_date)).reduce((s, e) => s + Number(e.revenue_usd), 0),
    [entries],
  );
  const monthlyActualUsd = useMemo(
    () => entries.filter(e => isThisMonth(e.entry_date)).reduce((s, e) => s + Number(e.revenue_usd), 0),
    [entries],
  );
  const weeklyTargetUsd = useMemo(() => {
    // sum of per-day targets across this week (use entry's target if present, else default)
    const week = entries.filter(e => isThisWeek(e.entry_date));
    const known = week.reduce((s, e) => s + Number(e.daily_target_usd ?? defaultDailyTargetUsd), 0);
    // assume 7 days of target so an empty week still has a denominator
    const missing = 7 - week.length;
    return known + Math.max(0, missing) * defaultDailyTargetUsd;
  }, [entries, defaultDailyTargetUsd]);
  const monthlyTargetUsd = useMemo(() => {
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    return defaultDailyTargetUsd * daysInMonth;
  }, [defaultDailyTargetUsd]);

  const chartData = useMemo(() => {
    const days = lastNDays(30);
    const map = new Map(entries.map(e => [e.entry_date, Number(e.revenue_usd)]));
    return days.map(d => ({
      date: d.slice(5), // MM-DD
      revenue: Math.round(convertFromUSD(map.get(d) ?? 0, city)),
      target: Math.round(convertFromUSD(defaultDailyTargetUsd, city)),
    }));
  }, [entries, defaultDailyTargetUsd, city]);

  const onTrackToday = today ? Number(today.revenue_usd) >= todayTargetUsd : false;
  const onTrackWeek = weeklyActualUsd >= weeklyTargetUsd;
  const onTrackMonth = monthlyActualUsd >= monthlyTargetUsd;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-medium text-foreground">Daily Revenue</h2>
          <p className="text-sm text-muted-foreground">Log today's numbers in under a minute. Compare actual vs budget.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              if (entries.length === 0) {
                toast.error("Log at least one day of revenue before exporting");
                return;
              }
              const sorted = [...entries].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
              const rows = sorted.map(e => {
                const target = e.daily_target_usd ?? defaultDailyTargetUsd;
                const variance = Number(e.revenue_usd) - target;
                const onTrack = Number(e.revenue_usd) >= target ? "On track" : "Below";
                return [
                  new Date(e.entry_date + "T00:00:00").toLocaleDateString(undefined, {
                    weekday: "short", month: "short", day: "numeric",
                  }),
                  fmt(e.revenue_usd),
                  fmt(target),
                  `${variance >= 0 ? "+" : ""}${fmt(Math.abs(variance)).replace(cs, cs)}`,
                  String(e.covers),
                  fmt(e.avg_spend_usd),
                  onTrack,
                ];
              });
              exportOperationsPdf(
                {
                  title: "Daily Revenue Report",
                  locationName: activeLocation?.name,
                  subtitle: `Last ${sorted.length} entries`,
                },
                [
                  {
                    heading: "Summary",
                    columns: ["Period", "Actual", "Target", "Status"],
                    alignments: ["left", "right", "right", "right"],
                    rows: [
                      ["Today", fmt(today ? Number(today.revenue_usd) : 0), fmt(todayTargetUsd), onTrackToday ? "On track" : "Below"],
                      ["This week", fmt(weeklyActualUsd), fmt(weeklyTargetUsd), onTrackWeek ? "On track" : "Below"],
                      ["This month", fmt(monthlyActualUsd), fmt(monthlyTargetUsd), onTrackMonth ? "On track" : "Below"],
                    ],
                  },
                  {
                    heading: "Daily entries",
                    columns: ["Date", "Revenue", "Target", "Variance", "Covers", "Avg spend", "Status"],
                    alignments: ["left", "right", "right", "right", "right", "right", "right"],
                    rows,
                  },
                ],
                `daily-revenue-${todayStr()}.pdf`,
              );
            }}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            <FileDown className="w-4 h-4" /> Export PDF
          </Button>
          <Button onClick={openAddDialog} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" /> {today ? "Edit Today" : "Log Today"}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          label="Today"
          actualUsd={today ? Number(today.revenue_usd) : 0}
          targetUsd={todayTargetUsd}
          onTrack={onTrackToday}
          fmt={fmt}
          icon={DollarSign}
        />
        <SummaryCard
          label="This Week"
          actualUsd={weeklyActualUsd}
          targetUsd={weeklyTargetUsd}
          onTrack={onTrackWeek}
          fmt={fmt}
          icon={TrendingUp}
        />
        <SummaryCard
          label="This Month"
          actualUsd={monthlyActualUsd}
          targetUsd={monthlyTargetUsd}
          onTrack={onTrackMonth}
          fmt={fmt}
          icon={Target}
        />
      </div>

      {/* 30-day trend chart */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="font-heading text-sm font-medium text-foreground mb-3">30-day revenue trend</h3>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={50} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number) => `${cs}${Number(v).toLocaleString()}`}
              />
              <Line type="monotone" dataKey="target" stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" dot={false} strokeWidth={1} />
              <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent entries */}
      <div>
        <h3 className="font-heading text-sm font-medium text-foreground mb-3">Recent entries</h3>
        {entries.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Users className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No entries yet. Log today to start tracking.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden">
            {entries.slice(0, 14).map(e => {
              const target = e.daily_target_usd ?? defaultDailyTargetUsd;
              const onTrack = Number(e.revenue_usd) >= target;
              return (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-1.5 h-8 rounded-full ${onTrack ? "bg-primary" : "bg-destructive"}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {new Date(e.entry_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {e.covers} covers · {fmt(e.avg_spend_usd)} avg spend
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-sm font-medium ${onTrack ? "text-foreground" : "text-destructive"}`}>{fmt(e.revenue_usd)}</p>
                      <p className="text-[11px] text-muted-foreground">target {fmt(target)}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(e.id)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      aria-label="Delete entry"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log daily revenue</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rev-date">Date</Label>
              <Input
                id="rev-date"
                type="date"
                value={draft.entry_date}
                onChange={e => setDraft(d => ({ ...d, entry_date: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rev-amount">Revenue ({cs})</Label>
                <Input
                  id="rev-amount"
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  value={draft.revenue}
                  onChange={e => {
                    const val = e.target.value;
                    setDraft(d => {
                      const covers = Number(d.covers) || 0;
                      const rev = Number(val) || 0;
                      const avg = covers > 0 ? (rev / covers).toFixed(2) : d.avg_spend;
                      return { ...d, revenue: val, avg_spend: avg };
                    });
                  }}
                />
              </div>
              <div>
                <Label htmlFor="rev-covers">Covers</Label>
                <Input
                  id="rev-covers"
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={draft.covers}
                  onChange={e => {
                    const val = e.target.value;
                    setDraft(d => {
                      const covers = Number(val) || 0;
                      const rev = Number(d.revenue) || 0;
                      const avg = covers > 0 ? (rev / covers).toFixed(2) : d.avg_spend;
                      return { ...d, covers: val, avg_spend: avg };
                    });
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rev-avg">Avg spend / cover ({cs})</Label>
                <Input
                  id="rev-avg"
                  type="number"
                  inputMode="decimal"
                  placeholder="auto"
                  value={draft.avg_spend}
                  onChange={e => setDraft(d => ({ ...d, avg_spend: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="rev-target" className="flex items-center justify-between">
                  <span>Daily target ({cs})</span>
                  <Badge variant="secondary" className="text-[10px]">prefilled</Badge>
                </Label>
                <Input
                  id="rev-target"
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  value={draft.target}
                  onChange={e => setDraft(d => ({ ...d, target: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({
  label, actualUsd, targetUsd, onTrack, fmt, icon: Icon,
}: {
  label: string;
  actualUsd: number;
  targetUsd: number;
  onTrack: boolean;
  fmt: (usd: number) => string;
  icon: React.ElementType;
}) {
  const pct = targetUsd > 0 ? Math.round((actualUsd / targetUsd) * 100) : 0;
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className={`text-xl font-medium ${onTrack ? "text-foreground" : "text-destructive"}`}>{fmt(actualUsd)}</p>
      <p className="text-xs text-muted-foreground mt-1">
        of {fmt(targetUsd)} target ·{" "}
        <span className={onTrack ? "text-primary" : "text-destructive"}>{pct}%</span>
      </p>
    </div>
  );
}